import React, { useEffect, useRef, useMemo, useCallback } from "react";
import * as echarts from "echarts/core";
import { LineChart } from "echarts/charts";
import {
  TooltipComponent,
  GridComponent,
  MarkLineComponent,
  LegendComponent,
} from "echarts/components";
import { CanvasRenderer } from "echarts/renderers";
import { formatNumberWithIntl } from "@wavvest/shared-utils";

echarts.use([
  LineChart,
  TooltipComponent,
  GridComponent,
  MarkLineComponent,
  LegendComponent,
  CanvasRenderer,
]);

interface IncomeExpenseData {
  year: number;
  income: number;
  expenses: number;
  netSavings: number;
  [key: string]: number;
}

type IndicatorType = {
  x: number;
  y: number;
  name: string;
  income: number;
  expenses: number;
  netSavings: number;
};

type ThemeColorsType = {
  backgroundColor: string;
  textColor: string;
  incomeColor: string;
  expenseColor: string;
  netSavingsColor: string;
};

type DataItemType = {
  name: string;
  key: keyof IndicatorType;
  color: string;
};

interface IncomeExpenseChartProps {
  data: IncomeExpenseData[];
  width?: number;
  height?: number;
  themeColors?: ThemeColorsType;
  indicator?: IndicatorType | null;
  setIndicator: (indicator: IndicatorType | null) => void;
}

const dataListOrder: DataItemType[] = [
  {
    name: "Income",
    key: "income",
    color: "#5359D8",
  },
  {
    name: "Expenses",
    key: "expenses",
    color: "#724189",
  },
  {
    name: "Net Savings",
    key: "netSavings",
    color: "#62B4BF",
  },
];

export const IncomeExpenseChart: React.FC<IncomeExpenseChartProps> = ({
  data,
  width,
  height,
  themeColors = {
    backgroundColor: "#222222",
    textColor: "#ABABAB",
    incomeColor: "#5359D8",
    expenseColor: "#724189",
    netSavingsColor: "#62B4BF",
  },
  indicator,
  setIndicator,
}) => {
  const chartRef = useRef<HTMLDivElement>(null);
  const chartInstance = useRef<echarts.ECharts | null>(null);

  useEffect(() => {
    if (data.length > 0 && !indicator) {
      setIndicator({
        x: 0,
        y: data[0].income,
        name: data[0].year.toString(),
        income: data[0].income,
        expenses: data[0].expenses,
        netSavings: data[0].netSavings,
      });
    }
  }, [data, indicator, setIndicator]);

  const option = useMemo(
    () => ({
      backgroundColor: themeColors.backgroundColor,
      tooltip: {
        trigger: "axis",
        axisPointer: {
          type: "line",
          label: {
            backgroundColor: themeColors.backgroundColor,
          },
        },
        formatter: function (params: any) {
          const dataIndex = params[0].dataIndex;
          const currentData = data[dataIndex];

          const orderedParams = [...params].sort((a, b) => {
            const aIndex = dataListOrder.findIndex(
              (item) => item.name === a.seriesName,
            );
            const bIndex = dataListOrder.findIndex(
              (item) => item.name === b.seriesName,
            );
            return aIndex - bIndex;
          });

          return orderedParams
            .map((item: any) => {
              const dataItem = dataListOrder.find(
                (d) => d.name === item.seriesName,
              );
              if (!dataItem) return "";
              const value = currentData[dataItem.key];
              return `${item.marker} ${item.seriesName}: ${value != null ? formatNumberWithIntl({ num: value }) : "-"}`;
            })
            .join("<br/>");
        },
      },
      grid: {
        left: "3%",
        right: "4%",
        bottom: "3%",
        containLabel: true,
      },
      xAxis: {
        type: "category",
        boundaryGap: false,
        data: data.map((item) => item.year),
        axisTick: { show: false },
        axisLine: {
          lineStyle: {
            color: themeColors.textColor,
          },
        },
        axisLabel: {
          color: themeColors.textColor,
        },
      },
      yAxis: {
        type: "value",
        axisLine: {
          lineStyle: {
            color: themeColors.textColor,
          },
        },
        axisLabel: {
          color: themeColors.textColor,
          formatter: (value: number) =>
            value != null ? formatNumberWithIntl({ num: value }) : "-",
        },
        splitLine: {
          lineStyle: {
            color: "#FFFFFF33",
            type: "dashed",
          },
        },
      },
      series: [
        {
          name: "Income",
          type: "line",
          symbol: "none",
          lineStyle: {
            width: 0,
          },
          itemStyle: {
            color: themeColors.incomeColor,
          },
          areaStyle: {
            color: new echarts.graphic.LinearGradient(0, 0, 0, 1, [
              {
                offset: 0,
                color: themeColors.incomeColor,
              },
              {
                offset: 1,
                color: themeColors.incomeColor + "00",
              },
            ]),
          },
          emphasis: {
            focus: "series",
          },
          data: data.map((item) => item.income),
        },
        {
          name: "Expenses",
          type: "line",
          symbol: "none",
          lineStyle: {
            width: 0,
          },
          itemStyle: {
            color: themeColors.expenseColor,
          },
          areaStyle: {
            color: new echarts.graphic.LinearGradient(0, 0, 0, 1, [
              {
                offset: 0,
                color: themeColors.expenseColor,
              },
              {
                offset: 1,
                color: themeColors.expenseColor + "00",
              },
            ]),
          },
          emphasis: {
            focus: "series",
          },
          data: data.map((item) => item.expenses),
        },
        {
          name: "Net Savings",
          type: "line",
          symbol: "none",
          lineStyle: {
            width: 0,
          },
          itemStyle: {
            color: themeColors.netSavingsColor,
          },
          areaStyle: {
            color: new echarts.graphic.LinearGradient(0, 0, 0, 1, [
              {
                offset: 0,
                color: themeColors.netSavingsColor,
              },
              {
                offset: 1,
                color: themeColors.netSavingsColor + "00",
              },
            ]),
          },
          emphasis: {
            focus: "series",
          },
          data: data.map((item) => item.netSavings),
          markLine: indicator
            ? {
                data: [
                  {
                    xAxis: indicator.name,
                  },
                ],
                animation: false,
                symbol: "none",
                lineStyle: {
                  color: themeColors.textColor,
                  type: [5, 10],
                },
                label: {
                  show: false,
                },
              }
            : undefined,
        },
      ],
    }),
    [data, themeColors, indicator],
  );

  const handleChartClick = useCallback(
    (event: any) => {
      if (!chartInstance.current) return;

      const pointInPixel = [event.offsetX, event.offsetY];
      const pointInGrid = chartInstance.current.convertFromPixel(
        { seriesIndex: 0 },
        pointInPixel,
      );

      if (pointInGrid) {
        const [xValue, yValue] = pointInGrid;
        const index = Math.round(xValue);

        if (index >= 0 && index < data.length) {
          const currentData = data[index];
          setIndicator({
            x: xValue,
            y: yValue,
            name: currentData.year.toString(),
            income: currentData.income,
            expenses: currentData.expenses,
            netSavings: currentData.netSavings,
          });
        }
      }
    },
    [data, setIndicator],
  );

  useEffect(() => {
    if (!chartRef.current) return;

    if (!chartInstance.current) {
      chartInstance.current = echarts.init(chartRef.current);
    }

    try {
      chartInstance.current.setOption(option);

      const zr = chartInstance.current.getZr();
      zr.off("click");
      zr.on("click", handleChartClick);
    } catch (error) {
      console.error("Error setting chart options:", error);
    }

    const handleResize = () => {
      chartInstance.current?.resize();
    };
    window.addEventListener("resize", handleResize);

    return () => {
      window.removeEventListener("resize", handleResize);
    };
  }, [option, handleChartClick]);

  useEffect(() => {
    return () => {
      if (chartInstance.current) {
        chartInstance.current.dispose();
        chartInstance.current = null;
      }
    };
  }, []);

  const indicatorContent = useMemo(() => {
    if (!indicator) return null;

    return (
      <div className="indicator-info">
        <div className="selected" style={styles.infoRow}>
          <div className="name" style={styles.item}>
            Year
          </div>
          <span className="value">{indicator.name}</span>
        </div>
        {dataListOrder.map((item) => {
          return (
            <div key={item.key} className="selected" style={styles.infoRow}>
              <div className="name" style={styles.item}>
                <div
                  style={{
                    ...styles.colorDot,
                    backgroundColor: item.color,
                  }}
                />
                <span>{item.name}</span>
              </div>
              <span className="value">
                {formatNumberWithIntl({
                  num: data[Math.round(indicator.x)][item.key],
                })}
              </span>
            </div>
          );
        })}
      </div>
    );
  }, [indicator, themeColors]);

  return (
    <div className="chart">
      <div className="title">
        <span className="title-name">Your income and expense projection</span>
      </div>
      <div
        ref={chartRef}
        style={{ width: width ? width : "100%", height: height ? height : 400 }}
      />
      {indicatorContent}
    </div>
  );
};

const styles = {
  infoRow: {
    display: "flex",
    justifyContent: "space-between",
    alignItems: "center",
  },
  item: {
    display: "flex",
    alignItems: "center",
    gap: "8px",
  },
  colorDot: {
    width: "16px",
    height: "16px",
    borderRadius: "50%",
  },
} as const;

export default IncomeExpenseChart;
